export interface UiItem {
  type: 'title' | 'line';
  id?: number;
  certifiedQuantity?: number;
  title?: string;
  serieConceptUid: any;
  conceptUid?: any;
  quantity?: number;
  price?: number;
  discount?: number;
  tax?: any[];
  masterUnitMeasurableUid?: any;
  quantityMeasurable?: number;
}

export interface LineGroupUpsertDto {
  clientKey: string;
  title: string;
  sortOrder: number;
}

export interface InvoiceLineUpsertDto {
  id: number;
  product: string;
  conceptUid: string | null;

  quantity: number;
  unityAmount: number;
  discount: number;

  sortOrder: number;
  groupClientKey: string | null;

  totalLine: number;
  taxes: any[];
}

function guid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function pickId(x: any): any {
  if (!x) return null;
  if (x.id != null) return x.id;
  if (x.uid != null) return x.uid;
  if (x.value != null) return x.value;
  return null;
}

// function resolveSelected(list: any[], value: any): any {
//   var id = pickId(value);
//   if (id == null) return { id: null, display: value };

//   if (!list) return null;
//   for (var i = 0; i < list.length; i++) {
//     if (list[i] && list[i].id === id) return list[i];
//   }
//   return { id: null, display: value };
// }

function resolveSelected(list: any[] | null | undefined, value: any): any {
  const id = pickId(value);

  // Si no hay id, NO metas el objeto crudo en display
  if (id == null) return { id: null, display: toDisplay(value) };

  // Si el "id" resultó ser un objeto/array (por x.value), trátalo como "sin id"
  const idType = typeof id;
  const comparableId = (idType === "string" || idType === "number" || idType === "boolean");
  if (!comparableId) return { id: null, display: toDisplay(value) };

  if (!list) return { id: null, display: toDisplay(value) }; // tu comportamiento original

  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].id === id) return list[i];
  }

  // No encontrado: conserva display “usable”
  return { id: null, display: toDisplay(value) };
}

function toDisplay(v: any): any {
  if (v == null) return v;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;

  if (typeof v === "object") {
    // intenta campos típicos “humanos”
    if (typeof v.display === "string") return v.display;
    if (typeof v.label === "string") return v.label;
    if (typeof v.name === "string") return v.name;
    if (typeof v.title === "string") return v.title;

    // fallback seguro
    try { return JSON.stringify(v); } catch { return String(v); }
  }

  return String(v);
}

export function buildGroupsAndLines(params: {
  items: UiItem[];
  concepts: any[];
  taxes: any[];
}): { lineGroups: LineGroupUpsertDto[]; invoiceLines: InvoiceLineUpsertDto[] } {
  const items = params && params.items ? params.items : [];
  const concepts = params && params.concepts ? params.concepts : [];
  const taxes = params && params.taxes ? params.taxes : [];

  // 1) grupos + mapa index->key
  const lineGroups: LineGroupUpsertDto[] = [];
  // var groupKeyByIndex: Array<string | null> = new Array(items.length);
  // for (var k = 0; k < groupKeyByIndex.length; k++) groupKeyByIndex[k] = null;

  let currentKey: string | null = null;
  let groupOrder = 0;

  // for (var i = 0; i < items.length; i++) {
  //   var it = items[i];
  //   if (!it) continue;

  //   if (it.type === 'title') {
  //     var title = it.title ? String(it.title).trim() : '';

  //     if (title.length === 0) {
  //       currentKey = null;
  //       continue;
  //     }

  //     currentKey = guid();
  //     lineGroups.push({ clientKey: currentKey, title: title, sortOrder: groupOrder++ });
  //     continue;
  //   }

  //   // line
  //   groupKeyByIndex[i] = currentKey;
  // }

  // 2) mapear líneas
  const invoiceLines: InvoiceLineUpsertDto[] = [];
  let lineOrder = 0;
  let groupTitle = null;

  for (let j = 0; j < items.length; j++) {
    const lineItem = items[j];

    if (lineItem.type === 'title') {
      const title = lineItem.title ? String(lineItem.title).trim() : '';

      if (title.length === 0) {
        currentKey = null;
        continue;
      }

      currentKey = guid();
      groupTitle = { clientKey: currentKey, title: title, sortOrder: groupOrder++ }
      lineGroups.push(groupTitle);
      continue;
    }

    // if (!lineItem || lineItem.type !== 'line') continue;

    const concept = resolveSelected(concepts, lineItem.conceptUid);

    const qty = Number(lineItem.quantity) || 0;
    const price = Number(lineItem.price) || 0;
    const discount = Number(lineItem.discount) || 0;

    const base = price * qty * (1 - discount / 100);

    const selectedTaxes = lineItem.tax ? lineItem.tax : [];
    const mappedTaxes: any[] = [];

    for (let t = 0; t < selectedTaxes.length; t++) {
      const tx = selectedTaxes[t];
      const taxId = pickId(tx);

      let full = null;
      for (let z = 0; z < taxes.length; z++) {
        if (taxes[z] && taxes[z].id === taxId) { full = taxes[z]; break; }
      }

      let rate = 0;
      if (full && full.metadata && full.metadata.import != null) rate = full.metadata.import;

      mappedTaxes.push({
        id: full ? full.id : 0,
        taxImport: base * (rate / 100),
        type: (full && full.metadata && full.metadata.type) ? full.metadata.type : 'IVA',
        name: full ? (full.display || '') : '',
        taxId: (full && full.metadata && full.metadata.id != null) ? full.metadata.id : null
      });
    }

    let totalTaxes = 0;
    for (let m = 0; m < mappedTaxes.length; m++) {
      totalTaxes += Number(mappedTaxes[m].taxImport) || 0;
    }

    invoiceLines.push({
      id: lineItem.id != null ? Number(lineItem.id) : 0,
      // El texto de la línea (con " - ISBN: …" o " - S/N: …"), no el nombre base del concepto.
      product: concept ? ((lineItem.conceptUid && lineItem.conceptUid.display) || concept.display || '') : lineItem.conceptUid.display,
      // conceptUid: concept ? (concept.id || concept.display) : lineItem.serieConceptUid ? lineItem.serieConceptUid : lineItem.conceptUid.display,
      conceptUid: concept ? (concept.id || null) : lineItem.serieConceptUid ? lineItem.serieConceptUid : lineItem.conceptUid.display,

      quantity: qty,
      unityAmount: price,
      discount: discount,

      sortOrder: lineOrder++,
      groupClientKey: groupTitle,

      totalLine: base + totalTaxes,
      taxes: mappedTaxes
    });
  }

  return { lineGroups: lineGroups, invoiceLines: invoiceLines };
}
